import React, { useState } from 'react';
import { MessageSquare, Plus, Trash2 } from 'lucide-react';
import { useNotes, useNoteMutations } from '../hooks/useGardenData';
import { formatDate, NOTE_TYPE_COLORS, NOTE_TYPE_LABELS, todayISO } from '../lib/utils';
import type { NoteType } from '../types';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Input, Select, Textarea } from './ui/Input';

const NOTE_TYPES = Object.keys(NOTE_TYPE_LABELS) as NoteType[];

interface NoteTimelineProps {
  entityType: 'plant' | 'bed';
  entityId: string;
}

export function NoteTimeline({ entityType, entityId }: NoteTimelineProps) {
  const { data: notes = [], isLoading } = useNotes(entityType, entityId);
  const { create, remove } = useNoteMutations(entityType, entityId);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ note_type: 'observation' as NoteType, title: '', body: '', note_date: todayISO() });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await create.mutateAsync({ ...form, title: form.title || null, body: form.body || null });
      setAdding(false);
      setForm({ note_type: 'observation', title: '', body: '', note_date: todayISO() });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <div className="text-sm text-gray-400 py-2">Loading notes…</div>;

  return (
    <div className="flex flex-col gap-3">
      <Button variant="outline" size="sm" onClick={() => setAdding((v) => !v)}>
        <Plus size={14} />
        {adding ? 'Cancel' : 'Add Note'}
      </Button>

      {adding && (
        <form onSubmit={handleSubmit} className="border border-garden-200 dark:border-garden-800 rounded-xl p-3 bg-garden-50 dark:bg-green-950/40 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Type"
              value={form.note_type}
              onChange={(e) => setForm((f) => ({ ...f, note_type: e.target.value as NoteType }))}
            >
              {NOTE_TYPES.map((t) => <option key={t} value={t}>{NOTE_TYPE_LABELS[t]}</option>)}
            </Select>
            <Input
              label="Date"
              type="date"
              value={form.note_date}
              onChange={(e) => setForm((f) => ({ ...f, note_date: e.target.value }))}
            />
          </div>
          <Input
            label="Title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Optional title"
          />
          <Textarea
            label="Body"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            placeholder="Write your observation…"
            rows={3}
          />
          <Button type="submit" size="sm" loading={saving}>Save Note</Button>
        </form>
      )}

      {notes.length === 0 && !adding ? (
        <div className="flex flex-col items-center gap-2 py-6 text-gray-400">
          <MessageSquare size={28} />
          <p className="text-sm">No notes yet</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {notes.map((note) => (
            <div key={note.id} className="border border-gray-100 dark:border-gray-800 rounded-xl p-3 bg-white dark:bg-gray-900 group relative">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={NOTE_TYPE_COLORS[note.note_type]}>{NOTE_TYPE_LABELS[note.note_type]}</Badge>
                  <span className="text-xs text-gray-500">{formatDate(note.note_date)}</span>
                </div>
                <button
                  onClick={() => remove.mutate(note.id)}
                  aria-label={`Delete note${note.title ? `: ${note.title}` : ''}`}
                  className="text-gray-300 hover:text-red-500 transition-colors opacity-40 sm:opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {note.title && <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-1">{note.title}</p>}
              {note.body && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-wrap">{note.body}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
